import type { Key, ReactNode } from 'react'
import { useMatch } from 'react-router-dom'

export type GraphRowClassName =
  | 'comparisonBreakdown'
  | 'linearReference'
  | 'forecast'
  | 'forecastTotal'
  | 'riskSummary'

const graphRowClassNames: Record<
  'baseline' | 'wis',
  Record<GraphRowClassName, string>
> = {
  baseline: {
    comparisonBreakdown: 'min-h-[475px]',
    linearReference: 'min-h-[340px] lg:col-span-2',
    forecast: 'min-h-[430px] lg:col-span-2',
    forecastTotal: 'min-h-[380px] lg:col-span-2',
    riskSummary: 'min-h-[489px]'
  },
  wis: {
    comparisonBreakdown: 'min-h-[492px]',
    linearReference: 'min-h-[360px] lg:col-span-2',
    forecast: 'min-h-[430px] lg:col-span-2',
    forecastTotal: 'min-h-[380px] lg:col-span-2',
    riskSummary: 'min-h-[604px]'
  }
}

export type GraphGridItem<TGraphType extends Key = Key> = {
  graphType: TGraphType
  rowClassName: GraphRowClassName
}

type GraphGridProps<TGraphType extends Key> = {
  items: GraphGridItem<TGraphType>[]
  children: (item: GraphGridItem<TGraphType>) => ReactNode
  testId: string
}

export const GraphGrid = <TGraphType extends Key>({
  items,
  children,
  testId
}: GraphGridProps<TGraphType>) => {
  const matchCompare = useMatch(
    '/risk-management/assessments/compare/:stateIds/*'
  )
  const matchAssessment = useMatch(
    '/risk-management/assessments/states/:stateId/assessments/:assessmentId/*'
  )
  const rowClassNames =
    matchCompare != null || matchAssessment != null
      ? graphRowClassNames.wis
      : graphRowClassNames.baseline

  return (
    <div
      className='relative grid grid-cols-1 gap-5 p-5 lg:grid-cols-2'
      data-testid={testId}
    >
      {items.map((item) => (
        <div
          key={item.graphType}
          className={`h-full ${rowClassNames[item.rowClassName]}`}
        >
          {children(item)}
        </div>
      ))}
    </div>
  )
}
